import * as RadixTooltip from '@radix-ui/react-tooltip';
import type { ReactNode } from 'react';

import { HasTooltipProviderContext } from './TooltipProvider.context';

/**
 * App-level Tooltip provider — web implementation. Mount ONCE near the root.
 *
 * What it buys: Radix tracks "a tooltip was open recently" on the provider, so
 * every {@link Tooltip} under one provider shares it. Hover trigger A, then
 * move to trigger B within `skipDelayDuration` and B opens INSTANTLY instead
 * of re-waiting its own delay — the standard hover-to-hover behaviour. Without
 * a shared provider each Tooltip is an island and always re-waits.
 *
 * `delayDuration` here is only the fallback for tooltips that don't pass their
 * own `delayMs` — `Tooltip` forwards `delayMs` to `Tooltip.Root`, which
 * overrides the provider value per instance.
 *
 * The native counterpart renders children unchanged (Radix is web-only) so
 * cross-platform roots can mount this on both.
 */
export function TooltipProvider({
  children,
  delayMs = 400,
  skipDelayMs = 300,
}: {
  children: ReactNode;
  /** Fallback delay for tooltips that don't set their own `delayMs`. */
  delayMs?: number;
  /** Grace window after a tooltip closes during which the next opens instantly. */
  skipDelayMs?: number;
}) {
  return (
    <RadixTooltip.Provider delayDuration={delayMs} skipDelayDuration={skipDelayMs}>
      <HasTooltipProviderContext.Provider value={true}>
        {children}
      </HasTooltipProviderContext.Provider>
    </RadixTooltip.Provider>
  );
}
